import React from 'react';
import { KeyboardAvoidingView, Platform, ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useHeaderHeight } from '@react-navigation/elements';

type KeyboardSafeViewProps = {
  children: React.ReactNode;
  style?: ViewStyle;
  /**
   * When true, includes the safe-area top inset in the keyboard offset
   * to account for typical modal presentation gaps on iOS.
   */
  isModal?: boolean;
  /**
   * Extra pixels to add to the computed keyboard offset
   * for custom headers or additional top UI chrome.
   */
  extraOffset?: number;
};

/**
 * KeyboardSafeView
 * Wrap screens with bottom inputs to lift content above the keyboard.
 *
 * Usage examples:
 *
 * Regular screen:
 * <KeyboardSafeView>
 *   <YourScreen />
 * </KeyboardSafeView>
 *
 * Modal screen (iOS):
 * <KeyboardSafeView isModal>
 *   <YourModalScreen />
 * </KeyboardSafeView>
 *
 * Custom header inside modal:
 * <KeyboardSafeView isModal extraOffset={48}>
 *   <YourModalWithHeader />
 * </KeyboardSafeView>
 */
export const KeyboardSafeView: React.FC<KeyboardSafeViewProps> = ({ children, style, isModal, extraOffset = 0 }) => {
  const insets = useSafeAreaInsets();
  const headerHeight = useHeaderHeight();

  const keyboardVerticalOffset = Platform.OS === 'ios'
    ? (headerHeight || 0) + (isModal ? (insets.top || 0) : 0) + (extraOffset || 0)
    : 0;

  return (
    <KeyboardAvoidingView
      style={[{ flex: 1 }, style]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={keyboardVerticalOffset}
    >
      {children}
    </KeyboardAvoidingView>
  );
};

export default KeyboardSafeView;


